import React from 'react';

const BellCurveIndicator = ({ percentile = 50, label = "Your Score" }) => {
  // Calculate position on the bell curve (0-100 maps to curve width)
  const position = Math.max(5, Math.min(95, percentile));
  
  // Generate smooth bell curve path
  const generateBellCurvePath = () => {
    const width = 300;
    const height = 100;
    const points = [];
    
    // Generate bell curve points using normal distribution formula
    for (let x = 0; x <= width; x += 2) {
      const normalizedX = (x / width) * 6 - 3; // Map to -3 to 3 (standard deviations)
      const y = Math.exp(-0.5 * normalizedX * normalizedX) * height * 0.9;
      points.push({ x, y: height - y });
    }
    
    // Create path string
    let path = `M 0 ${height}`;
    points.forEach((point, i) => {
      if (i === 0) {
        path += ` L ${point.x} ${point.y}`;
      } else {
        path += ` L ${point.x} ${point.y}`;
      }
    });
    path += ` L ${width} ${height} Z`;
    
    return path;
  };

  const curveWidth = 300;
  const curveHeight = 100;
  const markerX = (position / 100) * curveWidth;

  // Determine color based on percentile
  const getPercentileColor = () => {
    if (percentile >= 75) return { fill: '#22c55e', text: 'text-green-600', bg: 'bg-green-50' };
    if (percentile >= 50) return { fill: '#2563eb', text: 'text-blue-600', bg: 'bg-blue-50' };
    if (percentile >= 25) return { fill: '#eab308', text: 'text-yellow-600', bg: 'bg-yellow-50' };
    return { fill: '#ef4444', text: 'text-red-600', bg: 'bg-red-50' };
  };

  const colors = getPercentileColor();

  // Determine message based on percentile
  const getMessage = () => {
    if (percentile >= 90) return "Exceptional! You're among the top performers.";
    if (percentile >= 75) return "You score above average!";
    if (percentile >= 50) return "You're performing around average.";
    if (percentile >= 25) return "There's room for improvement.";
    return "Focus on building your AI capabilities.";
  };

  return (
    <div className={`rounded-xl p-6 ${colors.bg}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className={`font-bold ${colors.text}`}>{getMessage()}</h3>
        <span className={`text-2xl font-bold ${colors.text}`}>{percentile}th percentile</span>
      </div>

      {/* Bell Curve SVG */}
      <div className="relative">
        <svg 
          viewBox={`0 0 ${curveWidth} ${curveHeight + 30}`} 
          className="w-full h-auto"
          style={{ maxHeight: '150px' }}
        >
          {/* Gradient definition */}
          <defs>
            <linearGradient id={`bellGradient-${percentile}`} x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor={colors.fill} stopOpacity="0.8" />
              <stop offset="100%" stopColor={colors.fill} stopOpacity="0.3" />
            </linearGradient>
          </defs>
          
          {/* Bell curve fill */}
          <path
            d={generateBellCurvePath()}
            fill={`url(#bellGradient-${percentile})`}
            stroke={colors.fill}
            strokeWidth="2"
          />
          
          {/* Marker line */}
          <line
            x1={markerX}
            y1={curveHeight + 5}
            x2={markerX}
            y2={10}
            stroke={colors.fill}
            strokeWidth="3"
            strokeDasharray="6,4"
          />
          
          {/* Marker circle */}
          <circle
            cx={markerX}
            cy={10}
            r="6"
            fill={colors.fill}
          />
          
          {/* X-axis labels */}
          <text x="15" y={curveHeight + 25} className="text-xs fill-gray-500" fontSize="10">0%</text>
          <text x={curveWidth / 2 - 10} y={curveHeight + 25} className="text-xs fill-gray-500" fontSize="10">50%</text>
          <text x={curveWidth - 30} y={curveHeight + 25} className="text-xs fill-gray-500" fontSize="10">100%</text>
        </svg>
      </div>

      {/* Explanation text */}
      <p className="text-sm text-gray-600 mt-4 text-center">
        Your organization scores better than <span className={`font-bold ${colors.text}`}>{percentile}%</span> of all other organizations 
        that have completed this quickscan.
      </p>
      
      {/* Disclaimer */}
      <p className="text-xs text-gray-400 mt-2 text-center italic">
        Note: Scores are self-reported and may vary due to subjective perceptions.
      </p>
    </div>
  );
};

export default BellCurveIndicator;
